// Auth-bootstrap validation classifier.
//
// The AuthProvider validates a recovered session SERVER-SIDE (auth.getUser) to
// guard against a forged/tampered cached token (C12) — getSession() only decodes
// the stored JWT, it does not verify the signature. The bug this fixes: the old
// bootstrap treated ANY getUser() failure as "forged token → hard sign-out", so a
// TRANSIENT failure (offline, 5xx, or a request aborted when Chrome discards +
// reloads a backgrounded tab) bounced a perfectly valid user to /login.
//
// This classifier separates the two cases so the caller can react correctly:
//   - 'valid'        → a user came back; trust it.
//   - 'auth-invalid' → GoTrue rejected the token (401/403): forged or expired.
//                      The caller should try a refresh (expired access token with a
//                      still-valid refresh token) and only sign out if that fails.
//   - 'transient'    → could not reach/complete validation (network, 5xx, 408, 429,
//                      abort). NEVER sign out for this — keep the session.

export type ValidationVerdict = 'valid' | 'auth-invalid' | 'transient'

export function classifyValidation(
  user: unknown,
  error: { status?: number | null } | null | undefined,
): ValidationVerdict {
  if (user) return 'valid'
  const status = error?.status
  // Only 401/403 mean the token itself was rejected. Everything else — no status
  // (network/abort), 5xx (server blip), 408 (timeout), 429 (rate limit) — is
  // transient and must not cost a valid user their session.
  if (status === 401 || status === 403) return 'auth-invalid'
  return 'transient'
}
